import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Printer, Pencil, Eye, Trash2, BadgeCheck } from "lucide-react";
import api from "../api";
import { fmt } from "../utils";
import { useLang } from "../i18n";
import { toast } from "sonner";
import DocumentPreviewDialog from "../components/print/DocumentPreviewDialog";

export default function Sales() {
  const [rows, setRows] = useState([]);
  const [preview, setPreview] = useState(null);
  const { t } = useLang();
  const load = () => api.get("/sales").then((r) => setRows(r.data));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load().catch(() => {}); }, []);

  const generateIrn = async (bill) => {
    try {
      await api.post(`/compliance/einvoice/${bill.id}/generate`);
      toast.success(`IRN generated for ${bill.bill_no}`);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || "IRN generate nahi hua");
    }
  };

  const removeSale = async (bill) => {
    if (!window.confirm(`Delete invoice ${bill.bill_no}? Stock will only be restored when it is safe.`)) return;
    try {
      await api.delete(`/sales/${bill.id}`);
      toast.success(`Invoice ${bill.bill_no} deleted`);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || "Invoice delete nahi ho paaya");
    }
  };

  return (
    <div data-testid="sales-page" className="sgp-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="sgp-page-title">{t("sales")}</h1>
          <p className="sgp-page-subtitle">GST Tax Invoice — save hote hi stock se quantity minus hogi.</p>
        </div>
        <Link to="/sales/new" data-testid="new-sale-btn"
          className="sgp-primary-btn">
          <Plus className="h-4 w-4" /> {t("newSale")}
        </Link>
      </div>
      <div className="sgp-table-shell overflow-x-auto">
        <table className="w-full min-w-[860px]">
          <thead>
            <tr>
              <th className="sgp-th">Invoice No.</th><th className="sgp-th">Date</th><th className="sgp-th">Hospital</th>
              <th className="sgp-th">Order No.</th><th className="sgp-th text-right">Grand Total</th>
              <th className="sgp-th text-right">Received</th><th className="sgp-th text-right">Due</th><th className="sgp-th"></th>
            </tr>
          </thead>
          <tbody data-testid="sales-table">
            {rows.map((b) => (
              <tr key={b.id} data-testid={`sale-row-${b.id}`} className="sgp-table-row">
                <td className="sgp-td">
                  <p className="font-mono text-xs font-semibold text-slate-900">{b.bill_no}</p>
                  {b.edited_at && <p className="mt-1 text-[10px] font-semibold text-amber-700">Edited · {new Date(b.edited_at).toLocaleString("en-IN")} · {b.edited_by}</p>}
                  {b.irn_no && <p className="mt-1 text-[10px] font-semibold text-emerald-700">IRN · {b.irn_status || "Generated"}</p>}
                </td>
                <td className="sgp-td font-mono text-xs">{b.date}</td>
                <td className="sgp-td">{b.hospital_name}</td>
                <td className="sgp-td font-mono text-xs">{b.order_no || "-"}</td>
                <td className="sgp-td text-right font-mono">
                  {fmt(b.grand_total)}
                  {Number(b.returned_total || 0) > 0 && <p className="text-[10px] text-amber-700 mt-1">Return −{fmt(b.returned_total)}</p>}
                </td>
                <td className="sgp-td text-right font-mono text-emerald-700">{fmt(b.paid_amount)}</td>
                <td className="sgp-td text-right font-mono">
                  <span className={Number(b.balance_due ?? (b.grand_total - b.paid_amount)) > 0 ? "text-rose-600 font-semibold" : "text-slate-400"}>
                    {fmt(b.balance_due ?? (b.grand_total - b.paid_amount))}
                  </span>
                </td>
                <td className="sgp-td whitespace-nowrap">
                  {b.hospital_gstin && !b.irn_no && <button data-testid={`irn-sale-${b.id}`} type="button" onClick={() => generateIrn(b)}
                    className="p-1.5 rounded hover:bg-emerald-50 text-slate-500 hover:text-emerald-700 inline-block" title="Generate IRN via configured GST gateway"><BadgeCheck className="h-4 w-4" /></button>}
                  <button data-testid={`preview-sale-${b.id}`} type="button" onClick={() => setPreview({ kind: "sale", id: b.id, label: `Tax Invoice ${b.bill_no}` })}
                    className="p-1.5 rounded hover:bg-blue-50 text-slate-500 hover:text-blue-700 inline-block" title="Preview"><Eye className="h-4 w-4" /></button>
                  <Link data-testid={`edit-sale-${b.id}`} to={`/sales/${b.id}/edit`}
                    className="p-1.5 rounded hover:bg-amber-50 text-slate-500 hover:text-amber-700 inline-block" title="Edit with audit trail"><Pencil className="h-4 w-4" /></Link>
                  <Link data-testid={`print-sale-${b.id}`} to={`/print/sale/${b.id}`} target="_blank" rel="noreferrer"
                    className="p-1.5 rounded hover:bg-slate-100 text-slate-500 hover:text-teal-700 inline-block" title="Print / PDF"><Printer className="h-4 w-4" /></Link>
                  <button data-testid={`delete-sale-${b.id}`} type="button" onClick={() => removeSale(b)}
                    className="p-1.5 rounded hover:bg-rose-50 text-slate-500 hover:text-rose-700 inline-block" title="Delete"><Trash2 className="h-4 w-4" /></button>
                </td>
              </tr>
            ))}
            {!rows.length && <tr><td colSpan={8} className="sgp-td text-center py-10 text-slate-400">Abhi koi sales bill nahi.</td></tr>}
          </tbody>
        </table>
      </div>
      <DocumentPreviewDialog open={!!preview} onClose={() => setPreview(null)} kind={preview?.kind} id={preview?.id} label={preview?.label} />
    </div>
  );
}
